import * as React from "react";
import DocumentTitle from "../../misc/components/DocumentTitle";
import { Form } from "informed";
import { UserForm, UserFormState } from "./UserForm";
import { useParams } from "react-router-dom";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { GlobalAuthorization } from "../../misc/components/Authorization";
import { useSecurityContext } from "../../Core/SecurityContext";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { Role, Site } from "webportalclient";

export const EditUserScreen = () => {
    const { id } = useParams<{ id: string }>();
    const client = useWebPortalApiClient();
    const { currentUserPermissions } = useSecurityContext();
    const [initialValues, setInitialValues] = React.useState<UserFormState>();
    const title = "Edit User";

    React.useEffect(() => {      
        const mapToRoleOptions = (results: Role[]) => results.map(v => ({ label: `(${v.study?.name ?? "All Studies"}) ${v.name}`, value: v.id }));
        const mapToSiteOptions = (results: Site[]) => results.map(v => ({ label: `(${v.study.name}) ${v.number}`, value: v.id }));

        const getSitesOptions = async (sitesIds: string[]) => {
            if (!sitesIds || sitesIds.length === 0)
                return [];

            const sites = await client.client.getSites("id,number", undefined, undefined, "study($select=name)");
            return mapToSiteOptions(sites.value.filter(e => sitesIds.some(x => e.id == x)));
        }

        const getRolesOptions = async (rolesIds: string[]) => {
            const roleResult = await client.client.getRoles("id,name", undefined, undefined, "study($select=id,name)");
            const userRoles = roleResult.value.filter(r => rolesIds.includes(r.id));
            return mapToRoleOptions(userRoles);
        }

        const fillUserForm = async () => {
            if (id) {
                const user = await client.client.getUser(id);
                
                const formState: UserFormState = {
                    email: user.email,
                    password: null,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    roles: await getRolesOptions(user.roleIds),
                    sites: await getSitesOptions(user.siteIds),
                    timeZone: user.timeZoneId,
                    country: user.countryId,
                    spirotracUserGroup: user.spirotracUserGroupId,
                    registeredForMobileApp: user.isRegisteredForMobileApp,
                    isDisabled: user.isDisabled,
                };
                setInitialValues(formState);
            }
        }
        
        fillUserForm();

        return () => {
            client.abortController.abort();
        }
    }, [id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading User Data..." />;
    }
    
    return (
        <DocumentTitle title={`${title} | Vitalograph Web Portal`}>
            <GlobalAuthorization permissionId={PermissionEnum.EditUsers} currentPermissions={currentUserPermissions}>
                <Form initialValues={initialValues} noValidate>
                    <UserForm header={title} userId={id} />
                </Form>
            </GlobalAuthorization>
        </DocumentTitle>
    );
}
